import "./ProyectosCommon.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { MacroProyectoResponse } from "../../../shared/response/proyectos/MacroProyectoResponse";
import { CosechaResponse } from "../../../shared/response/proyectos/CosechaResponse";
import { CultivoResponse } from "../../../shared/response/proyectos/CultivoResponse";
import { MacroProyectoService } from "../../services/proyectos/MacroProyectoService";
import { CosechaService } from "../../services/proyectos/CosechaService";
import { CultivoService } from "../../services/proyectos/CultivoService";
import { ProyectoResponse } from "../../../shared/response/proyectos/ProyectoResponse";
import { ProyectoService } from "../../services/proyectos/ProyectoService";
import { MacroProyectoFormModal } from "./MacroProyectoFormModal";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { fmtCentavos, fmtPct } from "../../../shared/utils/proyectosFormat";
import { formatKg } from "../../../shared/utils/formatUtils";
import { proyectoDeepLink } from "./ProyectosPage";

const ESTADO_LABELS: Record<string, string> = { en_configuracion: "En configuración", activo: "Activo", cerrado: "Cerrado" };
const ESTADO_CLS: Record<string, string> = { en_configuracion: "borrador", activo: "ok", cerrado: "na" };

export class MacroProyectosPage extends BasePageComponent {
  private _all: MacroProyectoResponse[] = [];
  private _filtered: MacroProyectoResponse[] = [];
  private _expandedId: string | null = null;
  private _cosechas: CosechaResponse[] = [];
  private _cultivos: CultivoResponse[] = [];
  private _proyectos: ProyectoResponse[] = [];
  private _tbody!:   HTMLTableSectionElement;
  private _totEl!:   HTMLSpanElement;
  private _search!:  HTMLInputElement;
  private _selEstado!: HTMLSelectElement;
  private _selCosecha!: HTMLSelectElement;

  public getAppRoute(): string { return "/proyectos/macro-proyectos"; }
  public getPageTitle(): string | null { return "Proyectos"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page pr-page";
    pageContent.innerHTML = `
      <div class="pr-content">
        <div class="pr-pg-hd">
          <div>
            <div class="pr-pg-ey">Proyectos · Configuración</div>
            <div class="pr-pg-ti">Macro-Proyectos</div>
            <div class="pr-pg-su">Lotes de proyectos agrupados por cosecha y región</div>
          </div>
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="mp-nuevo"><i class="ph ph-plus"></i> Nuevo Macro-Proyecto</button>
        </div>

        <div class="pr-kstrip" id="mp-kstrip"></div>

        <div class="pr-frow">
          <div class="pr-sw">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input class="pr-si" id="mp-search" placeholder="Buscar nombre o región…">
          </div>
          <select class="pr-fsel" id="mp-estado">
            <option value="">Todos los estados</option>
            <option value="en_configuracion">En configuración</option>
            <option value="activo">Activo</option>
            <option value="cerrado">Cerrado</option>
          </select>
          <select class="pr-fsel" id="mp-cosecha"><option value="">Todas las cosechas</option></select>
          <span class="pr-ftot" id="mp-tot"></span>
        </div>

        <div class="pr-tbl-wrap">
          <table class="pr-tbl">
            <thead>
              <tr><th>Macro-Proyecto</th><th>Cosecha</th><th>Cultivos</th><th>Proyectos</th><th>Ha</th><th>Monto est.</th><th>Estado</th><th>Acciones</th></tr>
            </thead>
            <tbody id="mp-tbody"><tr><td colspan="8" class="pr-empty">Cargando…</td></tr></tbody>
          </table>
        </div>
      </div>
    `;

    this._tbody      = document.getElementById("mp-tbody")! as HTMLTableSectionElement;
    this._totEl      = document.getElementById("mp-tot")!;
    this._search     = document.getElementById("mp-search")! as HTMLInputElement;
    this._selEstado  = document.getElementById("mp-estado")! as HTMLSelectElement;
    this._selCosecha = document.getElementById("mp-cosecha")! as HTMLSelectElement;
    this._search.oninput = () => this._filterAndRender();
    this._selEstado.onchange = () => this._filterAndRender();
    this._selCosecha.onchange = () => this._filterAndRender();
    document.getElementById("mp-nuevo")!.onclick = () => this._onCreate();

    void this._loadData();
  }

  protected override beforeDispose(): void {}

  private async _loadData(): Promise<void> {
    try {
      const [items, cosechas, cultivos, proyectos] = await Promise.all([
        MacroProyectoService.instance.get(),
        CosechaService.instance.get().catch(() => [] as CosechaResponse[]),
        CultivoService.instance.get().catch(() => [] as CultivoResponse[]),
        // Solo para contar beneficiarios distintos del portafolio: sumar un conteo por macro
        // los duplicaría si un mismo cliente tiene proyectos en dos macro-proyectos.
        ProyectoService.instance.get().catch(() => [] as ProyectoResponse[]),
      ]);
      this._proyectos = proyectos.filter((p) => !p.isEliminado);
      this._all = items.filter((m) => !m.isEliminado).sort((a, b) => a.nombre.localeCompare(b.nombre));
      this._cosechas = cosechas.filter((c) => !c.isEliminado).sort((a, b) => b.anio - a.anio);
      this._cultivos = cultivos.filter((c) => !c.isEliminado).sort((a, b) => a.nombre.localeCompare(b.nombre));
      this._selCosecha.innerHTML = `<option value="">Todas las cosechas</option>` +
        this._cosechas.map((c) => `<option value="${c.id}">${this._esc(c.nombre)}</option>`).join("");
      this._renderKpis();
      this._filterAndRender();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar Macro-Proyectos", (error as Error).message);
      this._tbody.innerHTML = `<tr><td colspan="8" class="pr-empty">Error al cargar</td></tr>`;
    }
  }

  private _renderKpis(): void {
    const activos = this._all.filter((m) => m.estado === "activo").length;
    const enConfig = this._all.filter((m) => m.estado === "en_configuracion").length;
    const totalProyectos = this._all.reduce((s, m) => s + m.totalProyectos, 0);
    const totalHa = this._all.reduce((s, m) => s + m.totalHa, 0);
    const montoTotal = this._all.reduce((s, m) => s + m.montoTotal, 0);
    const beneficiarios = new Set(this._proyectos.filter((p) => p.macroProyectoId).map((p) => p.clienteId)).size;
    const strip = document.getElementById("mp-kstrip")!;
    strip.style.gridTemplateColumns = "repeat(5,1fr)";
    strip.innerHTML = `
      ${this._kpi(String(activos), "ACTIVOS", "ok", `${enConfig} en configuración`)}
      ${this._kpi(String(totalProyectos), "PROYECTOS", "blue", "bajo macros")}
      ${this._kpi(String(beneficiarios), "BENEFICIARIOS", "", "en portafolio")}
      ${this._kpi(formatKg(totalHa) + " ha", "HECTÁREAS", "", "área cultivable")}
      ${this._kpi(fmtCentavos(montoTotal), "MONTO PORTAFOLIO", "pr-k-val-money", "estimado COP")}
    `;
  }

  private _kpi(value: string, label: string, color: string, sub: string): string {
    return `<div class="pr-kcard"><div class="pr-k-val ${color}">${value}</div><div class="pr-k-lbl">${label}</div><div class="pr-k-sub">${sub}</div></div>`;
  }

  private _filterAndRender(): void {
    const q = this._search.value.toLowerCase().trim();
    const fe = this._selEstado.value;
    const fc = this._selCosecha.value;
    this._filtered = this._all.filter((m) => {
      const matchQ = !q || m.nombre.toLowerCase().includes(q) || m.region.toLowerCase().includes(q);
      const matchE = !fe || m.estado === fe;
      const matchC = !fc || m.cosechaId === fc;
      return matchQ && matchE && matchC;
    });
    this._render();
  }

  private _render(): void {
    const list = this._filtered;
    this._totEl.textContent = `${list.length} macro-proyecto${list.length === 1 ? "" : "s"}`;
    if (list.length === 0) {
      this._tbody.innerHTML = `<tr><td colspan="8" class="pr-empty">Sin resultados</td></tr>`;
      return;
    }
    this._tbody.innerHTML = list.map((m, i) => {
      const expanded = this._expandedId === m.id;
      const avance = m.montoTotal > 0 ? Math.round((m.ejecutadoTotal / m.montoTotal) * 100) : 0;
      const row = `
      <tr class="pr-mp-row${expanded ? " expanded" : ""}" data-mp-row="${m.id}">
        <td><div style="font-weight:var(--sx-weight-semibold)">${this._esc(m.nombre)}</div><div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">${this._esc(m.region)}</div></td>
        <td>
          ${this._esc(m.cosechaNombre)}<br>
          <span style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">
            ${fmtPct(m.cosechaTasaBaseIpc, 1)} IPC ${m.cosechaFrozen ? `<i class="ph ph-lock-simple" title="Congelado"></i>` : `<i class="ph ph-pencil-simple" title="Editable"></i>`}
          </span>
        </td>
        <td>${m.cultivoNombres.length ? m.cultivoNombres.map((c) => `<span class="pr-pill blue" style="margin:1px">${this._esc(c)}</span>`).join(" ") : `<span style="color:var(--sx-text-tertiary)">—</span>`}</td>
        <td class="pr-num">${m.totalProyectos}
          ${m.totalProyectos > 0 ? `<div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">${m.proyectosActivos} act · ${m.proyectosEnRevision} rev · ${m.proyectosAprobados} apr</div>` : ""}
        </td>
        <td class="pr-num">${formatKg(m.totalHa)}</td>
        <td class="pr-num">${fmtCentavos(m.montoTotal)}</td>
        <td><span class="pr-pill ${ESTADO_CLS[m.estado]}">${ESTADO_LABELS[m.estado]}</span></td>
        <td>
          <div class="pr-actions">
            <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-act="ver" data-idx="${i}" title="Ver proyectos"><i class="ph ph-eye"></i></button>
            ${m.estado === "en_configuracion" ? `<button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="activar" data-idx="${i}">Activar</button>` : ""}
            ${m.estado !== "cerrado" ? `<button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-act="editar" data-idx="${i}" title="Editar"><i class="ph ph-pencil-simple"></i></button>` : ""}
          </div>
        </td>
      </tr>`;
      if (!expanded) return row;
      return row + `
      <tr class="pr-mp-exp-row">
        <td colspan="8">
          <div class="pr-mp-exp-inner">
            <div class="pr-mp-exp-stat">
              <div class="pr-mp-exp-lbl">Proyectos activos</div>
              <div class="pr-mp-exp-val" style="color:var(--sx-success)">${m.proyectosActivos}</div>
              <div class="pr-mp-exp-sub">de ${m.totalProyectos} totales</div>
            </div>
            <div class="pr-mp-exp-stat">
              <div class="pr-mp-exp-lbl">En revisión</div>
              <div class="pr-mp-exp-val" style="color:var(--sx-warning)">${m.proyectosEnRevision}</div>
              <div class="pr-mp-exp-sub">pendientes de aprobación</div>
            </div>
            <div class="pr-mp-exp-stat">
              <div class="pr-mp-exp-lbl">Aprobados</div>
              <div class="pr-mp-exp-val" style="color:var(--sx-info)">${m.proyectosAprobados}</div>
              <div class="pr-mp-exp-sub">listos para activar</div>
            </div>
            <div class="pr-mp-exp-stat">
              <div class="pr-mp-exp-lbl">Avance general</div>
              <div class="pr-mp-exp-val">${avance}%</div>
              <div class="pr-mp-exp-bar"><div class="pr-mp-exp-bar-fill" style="width:${avance}%"></div></div>
            </div>
          </div>
          <div class="pr-mp-exp-actions">
            <button class="sx-btn sx-btn-primary sx-btn-sm" data-act="ver" data-idx="${i}">Ver proyectos <i class="ph ph-arrow-right"></i></button>
            ${m.estado === "en_configuracion" ? `<button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="activar" data-idx="${i}">Activar y congelar IPC</button>` : ""}
          </div>
        </td>
      </tr>`;
    }).join("");

    this._tbody.querySelectorAll<HTMLTableRowElement>("tr[data-mp-row]").forEach((tr) => {
      tr.onclick = (e) => {
        if ((e.target as HTMLElement).closest("button")) return;
        this._expandedId = this._expandedId === tr.dataset.mpRow ? null : tr.dataset.mpRow!;
        this._render();
      };
    });
    this._tbody.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((btn) => {
      const m = list[Number(btn.dataset.idx)];
      btn.onclick = (e) => {
        e.stopPropagation();
        if (btn.dataset.act === "editar")  this._onEdit(m);
        if (btn.dataset.act === "activar") this._onActivar(m);
        if (btn.dataset.act === "ver")      this._onVerProyectos(m);
      };
    });
  }

  private _onCreate(): void {
    new MacroProyectoFormModal(this._cosechas, this._cultivos, (saved) => {
      this._all = [saved, ...this._all].sort((a, b) => a.nombre.localeCompare(b.nombre));
      this._renderKpis();
      this._filterAndRender();
    }).init();
  }

  private _onEdit(m: MacroProyectoResponse): void {
    new MacroProyectoFormModal(this._cosechas, this._cultivos, (saved) => {
      this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
      this._renderKpis();
      this._filterAndRender();
    }, m).init();
  }

  private _onVerProyectos(m: MacroProyectoResponse): void {
    proyectoDeepLink.pendingMacroProyectoId = m.id;
    this.navigate("/proyectos/proyectos");
  }

  private _onActivar(m: MacroProyectoResponse): void {
    new ConfirmDialogComponent({
      title: "Activar Macro-Proyecto",
      message: m.cosechaFrozen
        ? `¿Activar "${m.nombre}"? La cosecha "${m.cosechaNombre}" ya está congelada.`
        : `¿Activar "${m.nombre}"? Esto también congela el IPC de la cosecha "${m.cosechaNombre}" — a partir de ahí no se puede editar ninguna tasa.`,
      confirmLabel: "Activar",
      onConfirm: async () => {
        try {
          const saved = await MacroProyectoService.instance.activar(m.id);
          this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
          this._renderKpis();
          this._filterAndRender();
          errorMessageComponent.addSuccess("Macro-Proyecto activado", `"${m.nombre}" ya está activo.`);
        } catch (error) {
          errorMessageComponent.addError("Error al activar", (error as Error).message);
        }
      },
    }).init();
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
